import {
  ContainerOutlined,
  PlusOutlined,
  FileTextOutlined,
  ScheduleOutlined,
} from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import SystemPrompt from '../SystemPrompt';
import DiagnosticTemplates from '../DiagnosticTemplates';
import ManageAnalyses from '../ManageAnalyses';
import ManageSchedules from '../ManageSchedules';
import Sessions from '../Sessions';
import ScheduledSessions from '../ScheduledSessions';
import { HeaderWrapper, StyledReportButton } from './styled';
import { SettingsMenu } from '../Settings/SettingsMenu';
import { SettingsModal } from '../Settings/SettingsModal';
import { ShareButton } from './ShareButton';
import { SaveAnalysisButton } from './SaveAnalysisButton';
import {
  isReportPanelOpenAtom,
  reportHighlightAtom,
  isDashboardGeneratedAtom,
  chatStreamLoadingAtom,
  reportTooltipCountAtom,
} from '../NexaStore';
import { FlexFill } from '@src/components/common/Utils/Layout';
import { useHasEvents } from '../hooks/useHasEvents';
import WhatsNewGate from './WhatsNewGate';

const { baseUrl } = window.conviva;

export const Options = () => {
  const [openListDrawer, setOpenListDrawer] = useState(false);
  const [openScheduledDrawer, setOpenScheduledDrawer] = useState(false);
  const [openPromptDrawer, setOpenPromptDrawer] = useState(false);
  const [openDiagnosticTemplatesDrawer, setOpenDiagnosticTemplatesDrawer] = useState(false);
  const [openManageAnalysesDrawer, setOpenManageAnalysesDrawer] = useState(false);
  const [openManageSchedulesDrawer, setOpenManageSchedulesDrawer] = useState(false);

  const reportHighlight = useAtomValue(reportHighlightAtom);
  const isLoading = useAtomValue(chatStreamLoadingAtom);
  const setIsReportPanelOpen = useSetAtom(isReportPanelOpenAtom);
  const setIsDashboardGenerated = useSetAtom(isDashboardGeneratedAtom);
  const setReportHighlight = useSetAtom(reportHighlightAtom);
  const tooltipCount = useAtomValue(reportTooltipCountAtom);
  const setTooltipCount = useSetAtom(reportTooltipCountAtom);
  const { hasEvents, isShareId } = useHasEvents();

  // Tooltip state for feature discovery
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // Track previous loading state to detect when stream completes
  const prevLoadingRef = useRef(isLoading);

  useEffect(() => {
    // Detect when loading goes from true to false (stream completed)
    if (prevLoadingRef.current === true && isLoading === false && hasEvents) {
      // Trigger report button highlight
      setReportHighlight((prev) => prev + 1);
      // Show tooltip if user hasn't seen it 3 times yet
      if (tooltipCount < 3) {
        setTooltipOpen(true);
      }
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading, hasEvents, setReportHighlight, tooltipCount]);

  // Auto-dismiss tooltip after 5 seconds and increment counter
  useEffect(() => {
    if (!tooltipOpen) return;

    const timer = setTimeout(() => {
      setTooltipOpen(false);
      setTooltipCount((prev) => prev + 1);
    }, 5000);
    return () => clearTimeout(timer);
  }, [tooltipOpen, setTooltipCount]);

  const navigate = useNavigate();

  const handleNewClick = () => {
    navigate(`${baseUrl}/dashboard/nexa`);
  };

  const handleSessionsClick = () => {
    setOpenListDrawer(true);
  };

  const handleScheduledClick = () => {
    setOpenScheduledDrawer(true);
  };

  const handleSystemPromptClick = () => {
    setOpenPromptDrawer(true);
  };

  const handleDiagnosticTemplatesClick = () => {
    setOpenDiagnosticTemplatesDrawer(true);
  };

  const handleManageAnalysesClick = () => {
    setOpenManageAnalysesDrawer(true);
  };

  const handleManageSchedulesClick = () => {
    setOpenManageSchedulesDrawer(true);
  };

  const handleOpenManageSchedulesFromScheduleDrawer = () => {
    setOpenManageAnalysesDrawer(false);
    setOpenManageSchedulesDrawer(true);
  };

  const handleReportClick = () => {
    setIsReportPanelOpen((prev) => {
      const newState = !prev;
      // If opening report panel, close dashboard
      if (newState) {
        setIsDashboardGenerated(false);
      }
      return newState;
    });
  };

  // Only show report button if there are events
  const showReportButton = hasEvents;

  return (
    <HeaderWrapper>
      <div style={{ display: 'flex', gap: '8px', flexDirection: 'row', flex: 1 }}>
        <Tooltip title="New Chat">
          <Button icon={<PlusOutlined />} type="primary" onClick={handleNewClick} />
        </Tooltip>
        <Tooltip title="Sessions">
          <Button icon={<ContainerOutlined />} onClick={handleSessionsClick} />
        </Tooltip>
        <Tooltip title="Scheduled Analysis Runs">
          <Button icon={<ScheduleOutlined />} onClick={handleScheduledClick} />
        </Tooltip>
        <SettingsMenu
          onSystemPromptClick={handleSystemPromptClick}
          onDiagnosticTemplatesClick={handleDiagnosticTemplatesClick}
          onManageAnalysesClick={handleManageAnalysesClick}
          onManageSchedulesClick={handleManageSchedulesClick}
        />
        <FlexFill />
        <SaveAnalysisButton />
        <ShareButton />
        {showReportButton && (
          <Tooltip
            title={tooltipOpen ? 'Try generating a report' : 'Reports'}
            open={tooltipOpen || undefined}
          >
            <StyledReportButton
              key={reportHighlight} // Key changes on each highlight trigger, forcing animation restart
              icon={<FileTextOutlined />}
              onClick={handleReportClick}
              $highlight={reportHighlight > 0}
            />
          </Tooltip>
        )}
        <Sessions open={openListDrawer} setOpen={setOpenListDrawer} />
        <ScheduledSessions open={openScheduledDrawer} setOpen={setOpenScheduledDrawer} />
        <SystemPrompt open={openPromptDrawer} setOpen={setOpenPromptDrawer} />
        <DiagnosticTemplates
          open={openDiagnosticTemplatesDrawer}
          setOpen={setOpenDiagnosticTemplatesDrawer}
        />
        <ManageAnalyses
          open={openManageAnalysesDrawer}
          setOpen={setOpenManageAnalysesDrawer}
          onRequestOpenManageSchedules={handleOpenManageSchedulesFromScheduleDrawer}
        />
        <ManageSchedules
          open={openManageSchedulesDrawer}
          setOpen={setOpenManageSchedulesDrawer}
          onRequestOpenManageSchedules={handleOpenManageSchedulesFromScheduleDrawer}
        />
      </div>
      <SettingsModal />
      <WhatsNewGate />
    </HeaderWrapper>
  );
};

export default Options;
